import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../utils/ThemeSwitcher';
import { FileText, GitBranch, ShieldCheck, Folder, Repeat, List } from 'lucide-react';
import './DataLoader.css';
 
/**
 * LoadXLSForm Component
 * Handles XLSForm/XLS survey definition file upload and variable matching
 * for skip logic analysis in Survey Flow
 */
const LoadXLSForm = ({ 
  onXLSFormLoaded, 
  isXLSFormLoaded,
  dataColumns = [],  // Columns from the primary data
  projectId, 
  onLoadingStart, 
  onLoadingEnd,
  xlsformData: existingXLSFormData  // For persistence
}) => {
  
  const { isDarkMode } = useTheme();
  
  
  const [isExpanded, setIsExpanded] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [parseResult, setParseResult] = useState(null);
  const [matchingResult, setMatchingResult] = useState(null);
  const [showMatchingUI, setShowMatchingUI] = useState(false);
  const [manualMatches, setManualMatches] = useState({});
  const [activeTab, setActiveTab] = useState('overview'); 
  const [loadedFileName, setLoadedFileName] = useState(null);
  const fileInputRef = useRef(null);
  
  const dataLoaded = isXLSFormLoaded || parseResult !== null || (existingXLSFormData !== null && existingXLSFormData !== undefined);
  
  
  useEffect(() => {
    if (existingXLSFormData) {
      console.log('Restoring XLSForm state from persistence:', existingXLSFormData);
      setParseResult(existingXLSFormData.parseResult || null);
      setMatchingResult(existingXLSFormData.matchingResult || null);
      setLoadedFileName(existingXLSFormData.fileName || null);
    }
  }, [existingXLSFormData]);
  
  
  useEffect(() => {
    if (existingXLSFormData?.parseResult) {
      setIsExpanded(true);
    }
  }, [existingXLSFormData]);
  
  // palette
  const colors = {
    surfaceBg: isDarkMode ? '#18181b' : '#f4f4f5',
    inputBg: isDarkMode ? '#18181b' : '#ffffff',
    border: isDarkMode ? '#27272a' : '#e4e4e7',
    borderHover: isDarkMode ? '#3f3f46' : '#d4d4d8',
    text: isDarkMode ? '#fafafa' : '#18181b',
    textSecondary: isDarkMode ? '#a1a1aa' : '#71717a',
    textMuted: isDarkMode ? '#71717a' : '#a1a1aa',
    primary: '#2563EB',
    primaryHover: '#1d4ed8',
    primaryBg: isDarkMode ? 'rgba(37, 99, 235, 0.12)' : 'rgba(37, 99, 235, 0.08)',
    success: '#2563EB',
    successBg: isDarkMode ? 'rgba(4, 35, 120, 0.15)' : 'rgba(4, 54, 120, 0.08)',
    warning: '#D97706',
    warningBg: isDarkMode ? 'rgba(217, 119, 6, 0.12)' : 'rgba(217, 119, 6, 0.08)',
    error: '#dc2626',
  };
 
  // Icons
  const XLSFormIcon = ({ size = 20, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z" 
        stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M14 2V8H20" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M8 13H16" stroke={color} strokeWidth="2" strokeLinecap="round"/>
      <path d="M8 17H13" stroke={color} strokeWidth="2" strokeLinecap="round"/>
      <circle cx="9" cy="10" r="1" fill={color}/>
    </svg>
  );
 
  const SkipLogicIcon = ({ size = 16, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9 3H5C3.89 3 3 3.89 3 5V9C3 10.11 3.89 11 5 11H9C10.11 11 11 10.11 11 9V5C11 3.89 10.11 3 9 3Z" 
        stroke={color} strokeWidth="2"/>
      <path d="M19 13H15C13.89 13 13 13.89 13 15V19C13 20.11 13.89 21 15 21H19C20.11 21 21 20.11 21 19V15C21 13.89 20.11 13 19 13Z" 
        stroke={color} strokeWidth="2"/>
      <path d="M11 7H13C14 7 15 8 15 9V13" stroke={color} strokeWidth="2" strokeLinecap="round"/>
      <path d="M12 16L15 13L18 16" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
 
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        '.xls',
        '.xlsx'
      ];
      const fileExtension = file.name.split('.').pop().toLowerCase();
      
      if (fileExtension === 'xls' || fileExtension === 'xlsx') {
        setSelectedFile(file);
        setError(null);
      } else {
        setError('Please select an XLS or XLSX file');
        setSelectedFile(null);
      }
    }
  };
 
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };
 
  const handleLoadXLSForm = async () => {
    if (!selectedFile) {
      setError('Please select an XLSForm file');
      return;
    }
 
    setLoading(true);
    setError(null);
    
    if (onLoadingStart) onLoadingStart();
 
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (projectId) {
        formData.append('project_id', projectId);
      }
      // Send data columns for auto-matching
      if (dataColumns && dataColumns.length > 0) {
        formData.append('data_columns', JSON.stringify(dataColumns));
      }
 
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      
      const response = await fetch(`${API_BASE}/api/xlsform/parse`, {
        method: 'POST',
        headers: {
          'user_email': userInfo.email || '',
        },
        body: formData,
      });
 
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to parse XLSForm');
      }
 
      const result = await response.json();
      
      setParseResult(result.xlsform_data);
      setMatchingResult(result.matching_result);
      setLoadedFileName(selectedFile.name);  // Store filename for persistence
      
      // If we have data columns and matching wasn't perfect, show matching UI
      if (dataColumns.length > 0 && result.matching_result?.summary?.match_rate < 1) {
        setShowMatchingUI(true);
        setActiveTab('matching');
      }
      
      // Notify parent
      if (onXLSFormLoaded) {
        onXLSFormLoaded({
          parseResult: result.xlsform_data,
          matchingResult: result.matching_result,
          fileName: selectedFile.name
        });
      }
 
    } catch (err) {
      console.error('XLSForm parsing error:', err);
      setError(err.message || 'Failed to parse XLSForm');
    } finally {
      setLoading(false);
      if (onLoadingEnd) onLoadingEnd();
    }
  };
 
  const handleManualMatch = (formField, dataColumn) => {
    setManualMatches(prev => ({
      ...prev,
      [formField]: dataColumn
    }));
  };
 
  const applyManualMatches = async () => {
    if (Object.keys(manualMatches).length === 0) return;
    
    setLoading(true);
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      
      const response = await fetch(`${API_BASE}/api/xlsform/update-mapping`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user_email': userInfo.email || '',
        },
        body: JSON.stringify({
          project_id: projectId,
          manual_matches: manualMatches,
          data_columns: dataColumns
        }),
      });
 
      if (response.ok) {
        const result = await response.json();
        setMatchingResult(result.matching_result);
        
        // Update parent
        if (onXLSFormLoaded) {
          onXLSFormLoaded({
            parseResult,
            matchingResult: result.matching_result,
            manualMatches
          });
        }
      }
    } catch (err) {
      console.error('Error updating mapping:', err);
    } finally {
      setLoading(false);
    }
  };
 
  // Render file upload section
  const renderFileUpload = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px',
      width: '100%',
      maxWidth: '400px',
      margin: '0 auto'
    }}>
      {/* File Type Info — eyebrow kicker style */}
      <div style={{
        fontSize: '10px',
        fontWeight: 500,
        letterSpacing: '0.16em',
        textTransform: 'lowercase',
        color: colors.primary,
        margin: '0 0 14px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
      }}>
        <span
          style={{
            width: '5px', height: '5px', borderRadius: '50%',
            background: colors.primary,
            boxShadow: '0 0 6px rgba(37, 99, 235, 0.5)',
            animation: 'dlBreathe 2.4s ease-in-out infinite',
          }}
        />
        upload your survey's xlsform definition (.xls / .xlsx)
      </div>
 
      {/* Drop Zone */}
      <div
        onClick={handleUploadClick}
        style={{
          width: '100%',
          padding: '32px 24px',
          border: `2px dashed ${selectedFile
            ? (isDarkMode ? 'rgba(52, 211, 153, 0.35)' : 'rgba(4, 120, 87, 0.35)')
            : (isDarkMode ? 'rgba(255, 255, 255, 0.14)' : 'rgba(0, 0, 0, 0.14)')}`,
          borderRadius: '10px',
          backgroundColor: selectedFile
            ? (isDarkMode ? 'rgba(52, 211, 153, 0.06)' : 'rgba(4, 120, 87, 0.04)')
            : (isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(255, 255, 255, 0.4)'),
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          textAlign: 'center',
        }}
        onMouseEnter={(e) => {
          if (!selectedFile) {
            e.currentTarget.style.borderStyle = 'solid';
            e.currentTarget.style.borderColor = colors.primary;
            e.currentTarget.style.backgroundColor = isDarkMode
              ? 'rgba(37, 99, 235, 0.08)'
              : 'rgba(37, 99, 235, 0.04)';
          }
        }}
        onMouseLeave={(e) => {
          if (!selectedFile) {
            e.currentTarget.style.borderStyle = 'dashed';
            e.currentTarget.style.borderColor = isDarkMode ? 'rgba(255, 255, 255, 0.14)' : 'rgba(0, 0, 0, 0.14)';
            e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(255, 255, 255, 0.4)';
          }
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xls,.xlsx"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        
        {selectedFile ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={colors.success} strokeWidth="2">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <span style={{ fontSize: '13px', fontWeight: '500', color: colors.success }}>
              {selectedFile.name}
            </span>
            <span style={{ fontSize: '12px', color: colors.textMuted }}>
              {(selectedFile.size / 1024).toFixed(1)} KB
            </span>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={colors.textMuted} strokeWidth="1.5">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            <span style={{ fontSize: '13px', color: colors.textSecondary }}>
              Click to select XLSForm file
            </span>
            <span style={{ fontSize: '12px', color: colors.textMuted }}>
              or drag and drop
            </span>
          </div>
        )}
      </div>
    </div>
  );
 
  // Render overview tab content
  const renderOverview = () => {
    if (!parseResult) return null;
    
    const summary = parseResult.summary || {};
    const formInfo = parseResult.form_info || {};
    const questions = parseResult.questions || {};
    
    // Calculate constraint statistics
    const constraintStats = Object.entries(questions).reduce((stats, [fieldName, question]) => {
      if (question.constraint && question.constraint.trim() && question.constraint.trim() !== 'nan') {
        stats.total++;
        // Categorize constraint types
        const constraint = question.constraint.toLowerCase();
        if (constraint.includes('>=') || constraint.includes('<=') || constraint.includes('>') || constraint.includes('<')) {
          stats.numeric++;
        }
        if (constraint.includes('regex')) {
          stats.regex++;
        }
        if (constraint.includes('.') && (constraint.includes('>=') || constraint.includes('<='))) {
          stats.range++;
        }
        stats.fields.push({
          name: fieldName,
          constraint: question.constraint,
          label: question.label || fieldName
        });
      }
      return stats;
    }, { total: 0, numeric: 0, regex: 0, range: 0, fields: [] });
    
    return (
      <div style={{ padding: '16px 0' }}>
        {/* Form Info */}
        {formInfo.form_title && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: colors.surfaceBg,
            borderRadius: '10px',
            marginBottom: '16px'
          }}>
            <div style={{ fontSize: '11px', color: colors.textMuted, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Form Title
            </div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: colors.text }}>
              {formInfo.form_title}
            </div>
          </div>
        )}
        
        {/* Stats Grid - Now 3x2 for skip logic + constraints */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '12px',
        }}>
          <StatCard 
            label="Questions"
            value={summary.total_questions || 0}
            icon={FileText}
            colors={colors}
          />
          <StatCard 
            label="With Skip Logic"
            value={summary.questions_with_skip_logic || 0}
            icon={GitBranch}
            colors={colors}
            highlight={summary.questions_with_skip_logic > 0}
          />
          <StatCard 
            label="With Constraints"
            value={constraintStats.total}
            icon={ShieldCheck}
            colors={colors}
            highlight={constraintStats.total > 0}
          />
          <StatCard 
            label="Groups"
            value={summary.total_groups || 0}
            icon={Folder}
            colors={colors}
          />
          <StatCard 
            label="Repeat Groups"
            value={summary.total_repeats || 0}
            icon={Repeat}
            colors={colors}
          />
          <StatCard 
            label="Choice Lists"
            value={summary.total_choice_lists || 0}
            icon={List}
            colors={colors}
          />
        </div>
        
        {/* Two-column layout for Skip Logic and Constraints */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: constraintStats.total > 0 && (parseResult.skip_logic_rules?.length > 0) ? '1fr 1fr' : '1fr',
          gap: '16px',
          marginTop: '20px'
        }}>
          {/* Skip Logic Rules Preview */}
          {parseResult.skip_logic_rules && parseResult.skip_logic_rules.length > 0 && (
            <div>
              <div style={{
                fontSize: '13px',
                fontWeight: '600',
                color: colors.text,
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <SkipLogicIcon size={14} color={colors.primary} />
                Skip Logic Rules ({parseResult.skip_logic_rules.length})
              </div>
              <div style={{
                maxHeight: '200px',
                overflowY: 'auto',
                borderRadius: '10px',
                border: `0.5px solid ${colors.border}`,
              }}>
                {parseResult.skip_logic_rules.slice(0, 5).map((rule, idx) => (
                  <div 
                    key={idx}
                    style={{
                      padding: '10px 12px',
                      borderBottom: idx < 4 ? `0.5px solid ${colors.border}` : 'none',
                      fontSize: '12px',
                    }}
                  >
                    <div style={{ fontWeight: '500', color: colors.text, marginBottom: '4px' }}>
                      {rule.target_question}
                    </div>
                    <div style={{ 
                      color: colors.textMuted, 
                      fontFamily: 'monospace',
                      fontSize: '11px',
                      wordBreak: 'break-all'
                    }}>
                      {rule.expression}
                    </div>
                  </div>
                ))}
                {parseResult.skip_logic_rules.length > 5 && (
                  <div style={{
                    padding: '10px 12px',
                    textAlign: 'center',
                    color: colors.textMuted,
                    fontSize: '12px'
                  }}>
                    +{parseResult.skip_logic_rules.length - 5} more rules
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Constraints Preview */}
          {constraintStats.total > 0 && (
            <div>
              <div style={{
                fontSize: '13px',
                fontWeight: '600',
                color: colors.text,
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={colors.success} strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                Validation Constraints ({constraintStats.total})
              </div>
              <div style={{
                maxHeight: '200px',
                overflowY: 'auto',
                borderRadius: '10px',
                border: `0.5px solid ${colors.border}`,
              }}>
                {constraintStats.fields.slice(0, 5).map((field, idx) => (
                  <div 
                    key={idx}
                    style={{
                      padding: '10px 12px',
                      borderBottom: idx < 4 ? `0.5px solid ${colors.border}` : 'none',
                      fontSize: '12px',
                    }}
                  >
                    <div style={{ fontWeight: '500', color: colors.text, marginBottom: '4px' }}>
                      {field.name}
                    </div>
                    <div style={{ 
                      color: colors.success, 
                      fontFamily: 'monospace',
                      fontSize: '11px',
                      wordBreak: 'break-all',
                      backgroundColor: isDarkMode ? 'rgba(4, 120, 87, 0.1)' : 'rgba(4, 120, 87, 0.08)',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      display: 'inline-block'
                    }}>
                      {field.constraint}
                    </div>
                  </div>
                ))}
                {constraintStats.fields.length > 5 && (
                  <div style={{
                    padding: '10px 12px',
                    textAlign: 'center',
                    color: colors.textMuted,
                    fontSize: '12px'
                  }}>
                    +{constraintStats.fields.length - 5} more constraints
                  </div>
                )}
              </div>
              
              {/* Constraint Type Summary */}
              {constraintStats.numeric > 0 && (
                <div style={{
                  marginTop: '12px',
                  padding: '8px 12px',
                  backgroundColor: isDarkMode ? 'rgba(4, 120, 87, 0.08)' : 'rgba(4, 120, 87, 0.05)',
                  borderRadius: '6px',
                  fontSize: '11px',
                  color: colors.textSecondary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={colors.success} strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  {constraintStats.numeric} numeric bounds available for outlier detection
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };
 
  // Render variable matching tab
  const renderMatchingTab = () => {
    if (!matchingResult) return null;
    
    const { matches, unmatched_form_fields, unmatched_data_columns, match_confidence, summary } = matchingResult;
    
    return (
      <div style={{ padding: '16px 0' }}>
        {/* Match Summary */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          padding: '12px 16px',
          backgroundColor: summary.match_rate >= 0.8 ? colors.successBg : colors.warningBg,
          borderRadius: '10px',
          marginBottom: '16px'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.5)',
            fontSize: '18px',
            fontWeight: '700',
            color: summary.match_rate >= 0.8 ? colors.success : colors.warning
          }}>
            {Math.round(summary.match_rate * 100)}%
          </div>
          <div>
            <div style={{ fontSize: '14px', fontWeight: '600', color: colors.text }}>
              {summary.matched} of {summary.total_form_fields} fields matched
            </div>
            <div style={{ fontSize: '12px', color: colors.textSecondary }}>
              {unmatched_form_fields.length} fields need manual mapping
            </div>
          </div>
        </div>
        
        {/* Unmatched Fields (need manual mapping) */}
        {unmatched_form_fields.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <div style={{
              fontSize: '13px',
              fontWeight: '600',
              color: colors.warning,
              marginBottom: '12px'
            }}>
              ⚠️ Unmatched Form Fields ({unmatched_form_fields.length})
            </div>
            <div style={{
              maxHeight: '200px',
              overflowY: 'auto',
              borderRadius: '10px',
              border: `0.5px solid ${colors.border}`,
            }}>
              {unmatched_form_fields.map((field, idx) => (
                <div 
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 12px',
                    borderBottom: idx < unmatched_form_fields.length - 1 ? `0.5px solid ${colors.border}` : 'none',
                  }}
                >
                  <span style={{ fontSize: '13px', color: colors.text, fontFamily: 'monospace' }}>
                    {field}
                  </span>
                  <select
                    value={manualMatches[field] || ''}
                    onChange={(e) => handleManualMatch(field, e.target.value)}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: `0.5px solid ${colors.border}`,
                      backgroundColor: colors.inputBg,
                      color: colors.text,
                      fontSize: '12px',
                      minWidth: '150px',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">Select column...</option>
                    {unmatched_data_columns.map(col => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            
            {Object.keys(manualMatches).length > 0 && (
              <button
                onClick={applyManualMatches}
                disabled={loading}
                style={{
                  marginTop: '12px',
                  padding: '10px 20px',
                  borderRadius: '10px',
                  border: 'none',
                  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryHover} 100%)`,
                  color: 'white',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? 'Applying...' : `Apply ${Object.keys(manualMatches).length} Manual Matches`}
              </button>
            )}
          </div>
        )}
        
        {/* Matched Fields */}
        <div>
          <div style={{
            fontSize: '13px',
            fontWeight: '600',
            color: colors.success,
            marginBottom: '12px'
          }}>
            ✓ Matched Fields ({Object.keys(matches).length})
          </div>
          <div style={{
            maxHeight: '200px',
            overflowY: 'auto',
            borderRadius: '10px',
            border: `0.5px solid ${colors.border}`,
          }}>
            {Object.entries(matches).slice(0, 20).map(([formField, dataCol], idx) => (
              <div 
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  borderBottom: idx < 19 ? `0.5px solid ${colors.border}` : 'none',
                  fontSize: '12px',
                }}
              >
                <span style={{ color: colors.textSecondary, fontFamily: 'monospace' }}>
                  {formField}
                </span>
                <span style={{ color: colors.textMuted }}>→</span>
                <span style={{ color: colors.text, fontFamily: 'monospace' }}>
                  {dataCol}
                </span>
                <span style={{
                  fontSize: '10px',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  backgroundColor: match_confidence[formField] >= 0.9 ? colors.successBg : colors.warningBg,
                  color: match_confidence[formField] >= 0.9 ? colors.success : colors.warning
                }}>
                  {Math.round((match_confidence[formField] || 0) * 100)}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };
 
  // Stat Card Component
  const StatCard = ({ label, value, icon: Icon, colors, highlight = false }) => (
    <div style={{
      padding: '12px',
      backgroundColor: colors.surfaceBg,
      borderRadius: '10px',
      border: `1px solid ${highlight ? colors.primary + '40' : colors.border}`,
    }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '6px',
        marginBottom: '4px'
      }}>
        {Icon && (
          <Icon
            size={13}
            strokeWidth={1.75}
            color={highlight ? colors.primary : colors.textMuted}
            style={{ flexShrink: 0 }}
          />
        )}
        <span style={{ fontSize: '11px', color: colors.textMuted, textTransform: 'uppercase' }}>
          {label}
        </span>
      </div>
      <div style={{ 
        fontSize: '20px', 
        fontWeight: '700', 
        color: highlight ? colors.primary : colors.text 
      }}>
        {value}
      </div>
    </div>
  );
 
  return (
    <div style={{ width: '100%' }}>
      {/* Expand/Collapse Header — unified dl-configure-row pattern */}
      <div
        className="dl-configure-row"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className="dl-configure-row-label">
          {dataLoaded ? 'xlsform loaded' : 'load xlsform (optional)'}
        </span>
        <div className={`dl-expand-chip ${isExpanded ? 'active' : ''}`}>
          <svg
            width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round"
          >
            <path d="M6 9l6 6 6-6"/>
          </svg>
        </div>
      </div>
 
      {/* Expandable Content */}
      <div style={{
        maxHeight: isExpanded ? '1000px' : '0px',
        overflow: 'hidden',
        transition: 'max-height 0.3s ease-in-out',
      }}>
        <div style={{ padding: '4px 0 8px' }}>
          {/* File Upload (if not loaded) */}
          {!parseResult && renderFileUpload()}
          
          {/* Tabs (if loaded) */}
          {parseResult && (
            <>
              <div style={{
                display: 'flex',
                gap: '4px',
                padding: '4px',
                backgroundColor: colors.surfaceBg,
                borderRadius: '10px',
                marginBottom: '16px'
              }}>
                {[
                  { id: 'overview', label: 'Overview' },
                  { id: 'matching', label: 'Variable Mapping' },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: activeTab === tab.id 
                        ? (isDarkMode ? '#27272a' : '#ffffff')
                        : 'transparent',
                      color: activeTab === tab.id ? colors.text : colors.textMuted,
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              
              {activeTab === 'overview' && renderOverview()}
              {activeTab === 'matching' && renderMatchingTab()}
              
              {/* Replace XLSForm Option */}
              <div style={{
                marginTop: '20px',
                paddingTop: '16px',
                borderTop: `0.5px solid ${colors.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <div style={{ fontSize: '12px', color: colors.textMuted }}>
                  Loaded: <span style={{ color: colors.textSecondary, fontWeight: '500' }}>
                    {loadedFileName || selectedFile?.name || 'Unknown file'}
                  </span>
                </div>
                <button
                  onClick={() => {
                    setParseResult(null);
                    setMatchingResult(null);
                    setSelectedFile(null);
                    setLoadedFileName(null);
                    setError(null);
                    setManualMatches({});
                    setActiveTab('overview');
                    // Notify parent to clear XLSForm data
                    if (onXLSFormLoaded) {
                      onXLSFormLoaded(null);
                    }
                  }}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: `0.5px solid ${colors.border}`,
                    backgroundColor: 'transparent',
                    color: colors.textSecondary,
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = colors.error;
                    e.currentTarget.style.color = colors.error;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = colors.border;
                    e.currentTarget.style.color = colors.textSecondary;
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                  </svg>
                  Replace XLSForm
                </button>
              </div>
            </>
          )}
          
          {/* Load Button (if file selected but not loaded) */}
          {!parseResult && selectedFile && (
            <div style={{ 
              display: 'flex',
              justifyContent: 'center',
              marginTop: '20px'
            }}>
              <button
                onClick={handleLoadXLSForm}
                disabled={loading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 28px',
                  borderRadius: '10px',
                  border: 'none',
                  background: loading 
                    ? '#3f3f46' 
                    : `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryHover} 100%)`,
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: loading ? 'none' : `0 4px 12px rgba(37, 99, 235, 0.25)`,
                }}
              >
                {loading && (
                  <svg className="spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                  </svg>
                )}
                {loading ? 'Parsing XLSForm...' : 'Parse XLSForm'}
              </button>
            </div>
          )}
          
          {/* Error Display */}
          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 16px',
              marginTop: '16px',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '0.5px solid rgba(239, 68, 68, 0.2)',
              borderRadius: '10px',
              color: colors.error,
              fontSize: '13px'
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={colors.error} strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {error}
            </div>
          )}
          
          {/* Info Box */}
          {!parseResult && !selectedFile && (
            <div style={{
              marginTop: '20px',
              padding: '12px 16px',
              backgroundColor: isDarkMode ? 'rgba(217, 119, 6, 0.08)' : 'rgba(217, 119, 6, 0.05)',
              borderRadius: '10px',
              border: `0.5px solid ${isDarkMode ? 'rgba(217, 119, 6, 0.24)' : 'rgba(217, 119, 6, 0.18)'}`,
            }}>
              <div style={{
                fontSize: '11px',
                color: isDarkMode ? '#fbbf24' : '#b45309',
                fontWeight: 500,
                letterSpacing: '0.06em',
                textTransform: 'lowercase',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18h6"/><path d="M10 22h4"/>
                  <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/>
                </svg>
                why upload an xlsform?
              </div>
              <div style={{ fontSize: '12px', color: colors.textSecondary, lineHeight: '1.5' }}>
                Uploading your survey's XLSForm definition enables skip logic validation in the Survey Flow analysis. 
                DataQuad will detect when questions were answered that should have been skipped based on the form's <code style={{ 
                  backgroundColor: colors.surfaceBg, 
                  padding: '1px 4px', 
                  borderRadius: '3px',
                  fontSize: '11px'
                }}>relevant</code> column.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
 
export default LoadXLSForm;